/**
 * Use Case: Fetch User Medias
 *
 * Orchestrates fetching paginated user medias from Firestore.
 * Part of clean architecture: Presentation → UseCase → Service → Firestore
 *
 * Matches Flutter's getUserMedias flow from MediasRepository
 */

import { UserMediaService } from '@/domain/services/userMedia/userMediaService';
import { UserMediaRemapper } from '@/domain/remappers/userMedia/userMediaRemapper';
import type { PageCursor } from '@/data/types/json';
import { SERVER_FETCH_CONSTANTS } from "@/domain/constants/serverFetchConstants";
import type { UserMediasResponse } from '@/domain/apiResponses/medias/userMediasResponse';
import { getAuthFirebaseUserUseCase } from '@/domain/usecases/user/getAuthFirebaseUserUseCase';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import { shouldFilterOutScheduledMediaUseCase, type HasActiveSubscriptionPredicate } from '@/domain/usecases/userMedia/shouldFilterOutScheduledMediaUseCase';
import { shouldFilterOutBlockedOrHiddenMediaUseCase } from '@/domain/usecases/userMedia/shouldFilterOutBlockedOrHiddenMediaUseCase';
import { appLogger } from '@/domain/services/analytics/appLogger';

export class FetchUserMediasUseCase {
    private userMediaService: UserMediaService;

    constructor() {
        this.userMediaService = UserMediaService.getInstance();
    }

    /**
     * Execute the use case
     *
     * @param userId - Firebase user ID to fetch medias for
     * @param currentUser - The authenticated user (from Redux) used for client-side
     *   filtering of blocked / hidden / scheduled content. Pass `null` if unavailable
     *   — filters that depend on it will be skipped.
     * @param hasActiveSubscription - Predicate `(creatorUserId) => boolean` sourced
     *   from the exclusiveContent slice. Use cases cannot import Redux directly,
     *   so hooks pass this in via useStore + selectHasActiveSubscription.
     * @param cursor - Pagination cursor
     * @param limit - Number of items to fetch (default: 20)
     * @returns Paginated user medias
     */
    async execute(
        userId: string,
        currentUser: UserEntity | null,
        hasActiveSubscription: HasActiveSubscriptionPredicate,
        cursor?: PageCursor,
        limit: number = SERVER_FETCH_CONSTANTS.DEFAULT_LIMIT
    ): Promise<UserMediasResponse> {
        try {
            const currentUserId = currentUser?.firebaseId ?? '';

            const result = await this.userMediaService.getUserMedias(
                userId,
                currentUserId,
                limit,
                cursor,
            );

            // Map DTOs to entities
            let medias: UserMediaEntity[] = result.items.map(item => UserMediaRemapper.toEntity(item));

            // Client-side filters matching Flutter's getUserMedias in content_data_source.dart

            // Filter out blocked-by-creator and hidden content (shared rule)
            medias = medias.filter(m => !shouldFilterOutBlockedOrHiddenMediaUseCase.execute(m, currentUser));

            // Filter out scheduled / early-access content for non-owners
            if (currentUserId !== userId) {
                const currentFirebaseUser = getAuthFirebaseUserUseCase.execute();
                medias = medias.filter(m => !shouldFilterOutScheduledMediaUseCase.execute(m, currentFirebaseUser?.uid, hasActiveSubscription));
            }

            return {
                medias,
                hasMore: result.hasMore,
                cursor: result.cursor,
            };
        } catch (error) {
            appLogger.error('Error fetching user medias', 'FetchUserMediasUseCase.execute', { error: error as Error });
            throw error;
        }
    }
}

/**
 * Factory function to create a new FetchUserMediasUseCase instance
 *
 * @example
 * const useCase = createFetchUserMediasUseCase();
 * const result = await useCase.execute(userId);
 */
export const createFetchUserMediasUseCase = (): FetchUserMediasUseCase => {
    return new FetchUserMediasUseCase();
};
